import type { Viewer } from '../../viewer/Viewer';
import * as THREE from 'three/webgpu';
import { AssemblyComponentEditMaterialFactory } from '../../material/AssemblyComponentEditMaterialFactory';
import { isImportedModelTopologyHost } from '../../imported-model/topology/imported-model-topology-runtime';


/**
 * 接收当前被编辑的零部件对象 ID。
 * 遍历当前导入装配体里的可见 mesh。
 * 目标零部件保持不透明。
 * 其它零部件切到透明显示。
 * 退出编辑零部件时恢复所有原始材质。
 * 负责 dispose，避免克隆材质泄漏。
 */
export class AssemblyComponentEditController {
  private readonly viewer: Viewer;
  private editingAssemblyObject: THREE.Object3D | null = null; // 正在被编辑的装配体对象
  private editingComponentObject: THREE.Object3D | null = null; // 正在被编辑的零部件对象
  /** 仅保存当前编辑上下文；正式特征归属由命令参数持久化。 */
  private documentScope: FeatureDocumentScope | null = null;

  /** @returns 当前零件范围快照；活动装配编辑缺少权威范围时禁止跨零件求值。 */
  public getDocumentScope(): FeatureDocumentScope | null {
    if (this.editingComponentObject && !this.documentScope) throw new Error('当前编辑零件缺少稳定的文档几何范围。');
    return this.documentScope ? structuredClone(this.documentScope) : null;
  }
  private subObjectList: THREE.Object3D[] = []; // 装配体对象的所有子对象（包括零件和装配体）
  private readonly originalMaterialByMesh: Map<THREE.Mesh, THREE.Material | THREE.Material[]> = new Map(); // 缓存装配体可见 mesh 的原始材质
  private readonly temporaryMaterials: Set<THREE.Material> = new Set(); // 编辑态创建的临时材质，退出时统一释放


  public constructor(viewer: Viewer) {
    this.viewer = viewer;
  }

  // 修改所有零部件的外观：
  // 目标零部件保持不透明。
  // 其它零部件切到灰色半透明显示。
  public modifyAllComponentsAppearance(componentObjectId: string, documentScope?: FeatureDocumentScope): void {
    // 重入时先恢复旧编辑态，避免把临时材质误缓存为原始材质。
    this.restoreAllComponentsAppearance();
    // 根据传入的零件的objectId，
    // ①获取objectId对应的对象
    const editingComponentObject = this.viewer.findSceneObjectById(componentObjectId);
    if (!editingComponentObject) {
      return;
    }
    this.editingComponentObject = editingComponentObject;
    this.documentScope = documentScope ? structuredClone(documentScope) : null;
    // ②查询其的父级对象（是一个装配体对象）
    const parentAssemblyObject = editingComponentObject.parent;
    if (!parentAssemblyObject) {
      return;
    }
    this.editingAssemblyObject = parentAssemblyObject;
    // ③遍历装配体对象的所有子对象（包括零件和装配体）
    this.subObjectList = parentAssemblyObject.children;
    // 遍历装配体对象的所有子对象，修改其的外观
    for (const subObject of this.subObjectList) {
      if (subObject === this.editingComponentObject) {
        const tempMaterial = AssemblyComponentEditMaterialFactory.createOpaqueGrayMaterial();
        this.temporaryMaterials.add(tempMaterial);
        this.setSubObjectsMaterial(subObject, tempMaterial, true);
      } else {
        const tempMaterial = AssemblyComponentEditMaterialFactory.createTransparentGrayMaterial();
        this.temporaryMaterials.add(tempMaterial);
        this.setSubObjectsMaterial(subObject, tempMaterial, true);
      }
    }
  }

  // 修改设计树选中零部件的外观：
  // 被选中的外观变为蓝色不透明（包括正在被编辑的零部件和非正在被编辑的零部件）
  public modifyDesignTreeSelectedComponentAppearance(componentObjectId: string): void {

  }

  // 修改编辑空间中鼠标拾取零部件的外观：
  // hover，橙色的透明相框
  // 点击选中正在被编辑的零部件，蓝色不透明外观+线框不被遮挡
  // 点击选中非正在被编辑的零部件，蓝色的透明外观+蓝色线框
  public modifyMousePickedComponentAppearance(componentObjectId: string): void {
  }

  /**
   * 退出编辑零部件时恢复所有原始材质。
   * 将装配体下所有子对象的材质恢复为其原有的正常查看状态（通常为标准材质）。
   * 注意：必须确保材质不要泄漏（要合理dispose），且只有在退出编辑状态时才能还原。
   */
  public restoreAllComponentsAppearance(): void {
    // 没有正在编辑的装配体和零部件则直接返回
    if (!this.editingAssemblyObject || !this.subObjectList.length) {
      this.clearRuntimeState();
      return;
    }
    // 退出编辑态前先清理交互高亮，避免状态机把编辑态透明材质当作 hover/selected 基准写回。
    this.clearViewerInteractionState();
    // 按实际 Mesh 恢复原始材质，避免用装配体子节点反查导致恢复失败。
    const restoredMeshes = this.restoreCachedMaterials();
    restoredMeshes.forEach((mesh) => {
      this.viewer.syncObjectBaseVisualState(mesh);
    });
    // 清空内部缓存引用
    this.clearRuntimeState();
  }

  // 设置Threejs对象下所有子对象的材质
  private setSubObjectsMaterial(object: THREE.Object3D, material: THREE.Material, isCache: boolean = false): void {
    // 找到对应的mesh对象，设置其的材质
    object.traverse((child: THREE.Object3D) => {
      if (!(child instanceof THREE.Mesh)) {
        return;
      }
      if (isImportedModelTopologyHost(child)) {
        return;
      }
      // 缓存原始材质
      if (isCache && !this.originalMaterialByMesh.has(child)) {
        this.originalMaterialByMesh.set(child, child.material);
      }
      child.material = material;
    });
  }

  /**
   * 恢复已缓存的原始材质。
   *
   * @returns 已恢复材质的 mesh 集合。
   */
  private restoreCachedMaterials(): THREE.Mesh[] {
    const restoredMeshes: THREE.Mesh[] = [];
    this.originalMaterialByMesh.forEach((material, mesh) => {
      mesh.material = material;
      restoredMeshes.push(mesh);
    });
    return restoredMeshes;
  }

  /**
   * 清理 Viewer 交互状态。
   *
   * 说明：编辑态期间的 hover/selected 可能把临时透明材质记录为交互状态机快照，
   * 因此必须先清掉交互态，再恢复并同步真实基础材质。
   */
  private clearViewerInteractionState(): void {
    this.viewer.clearSelectionProgrammatically();
    this.viewer.setProgrammaticHover(null);
    this.viewer.setProgrammaticHoverTargets([]);
  }

  /**
   * 清理编辑态运行时缓存和临时材质。
   */
  private clearRuntimeState(): void {
    this.documentScope = null;
    // 清空内部缓存引用
    this.editingComponentObject = null;
    this.editingAssemblyObject = null;
    this.subObjectList = [];
    this.temporaryMaterials.forEach((material) => {
      material.dispose();
    });
    this.temporaryMaterials.clear();
    this.originalMaterialByMesh.clear();
  }

  // 负责 dispose，避免克隆材质泄漏。
  public dispose(): void {
    // Viewer 销毁等外部释放路径也必须先恢复外观，避免模型停留在编辑态材质。
    this.clearViewerInteractionState();
    this.restoreCachedMaterials();
    this.clearRuntimeState();
  }
}
